import type { ProjectHandle, ProjectSource, ProjectSourceCapabilities } from "@loumy/contracts";
import { baseName, dirOf, globPrefix, globToRegExp, normalizeProjectPath, pathSegments } from "./paths";

// D69 — `FileSystemAccessSource`: Browser-Adapter über die File System Access API (showDirectoryPicker).
// Zero-Install, aber Chromium-only; KEIN Watch/Generate (Code-Parsing geht nicht im Browser → Manifest
// muss vorab committet sein). Sandbox: ALLE Pfade laufen über normalizeProjectPath ⇒ kein Ausbruch aus
// dem gewählten Projekt-Root. D47/D61 gewahrt — gelesen/geschrieben wird die LOKALE Platte, nie die Cloud-DB.
//
// Die Walk-/I/O-Funktionen sind als reine Funktionen über einem DirectoryHandle ausgelagert (unit-testbar
// mit einem In-Memory-Handle); die Klasse hält nur den Root + erfüllt den ProjectSource-Port.

const FS_CAPABILITIES: ProjectSourceCapabilities = { write: true, watch: false, generate: false, run: false };

export function isFileSystemAccessSupported(): boolean {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

/** Steigt zu dem Verzeichnis ab, das die gegebenen Segmente bezeichnet. `create` legt fehlende an. */
async function walkToDir(
  root: FileSystemDirectoryHandle,
  segments: string[],
  create: boolean,
): Promise<FileSystemDirectoryHandle> {
  let dir = root;
  for (const seg of segments) dir = await dir.getDirectoryHandle(seg, { create });
  return dir;
}

/** Liest eine Datei (Pfad sandboxed) als Bytes. */
export async function fsReadFile(root: FileSystemDirectoryHandle, path: string): Promise<Uint8Array> {
  const segs = pathSegments(path);
  const parent = await walkToDir(root, segs.slice(0, -1), false);
  const fileHandle = await parent.getFileHandle(segs[segs.length - 1]!, { create: false });
  const file = await fileHandle.getFile();
  return new Uint8Array(await file.arrayBuffer());
}

/** Schreibt eine Datei (Pfad sandboxed); fehlende Verzeichnisse werden angelegt. */
export async function fsWriteFile(root: FileSystemDirectoryHandle, path: string, data: Uint8Array): Promise<void> {
  const segs = pathSegments(path);
  const parent = await walkToDir(root, segs.slice(0, -1), true);
  const fileHandle = await parent.getFileHandle(segs[segs.length - 1]!, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    // Eine Kopie mit exaktem Längen-Slice schreiben (keine Anhänge aus einem größeren Backing-Buffer).
    await writable.write(data.slice());
  } finally {
    await writable.close();
  }
}

/** Entfernt eine Datei (Pfad sandboxed). No-op-sicher, wenn der Aufrufer Existenz nicht garantiert. */
export async function fsRemoveFile(root: FileSystemDirectoryHandle, path: string): Promise<void> {
  const segs = pathSegments(path);
  const parent = await walkToDir(root, segs.slice(0, -1), false);
  await parent.removeEntry(segs[segs.length - 1]!);
}

/** Listet Dateipfade unter dem literalen Glob-Präfix-Teilbaum und filtert auf den Glob. */
export async function fsList(root: FileSystemDirectoryHandle, glob: string): Promise<string[]> {
  const prefix = globPrefix(glob);
  let base = root;
  try {
    if (prefix) base = await walkToDir(root, prefix.split("/"), false);
  } catch {
    return []; // Präfix-Verzeichnis existiert (noch) nicht → keine Treffer.
  }
  const re = globToRegExp(glob); // EINMAL kompilieren (nicht je Datei) — Längen-Cap in globToRegExp.
  const out: string[] = [];
  const walk = async (dir: FileSystemDirectoryHandle, rel: string): Promise<void> => {
    for await (const [name, handle] of dir.entries()) {
      const childRel = rel ? `${rel}/${name}` : name;
      if (handle.kind === "directory") {
        await walk(handle as FileSystemDirectoryHandle, childRel);
      } else if (re.test(childRel)) {
        out.push(childRel);
      }
    }
  };
  await walk(base, prefix);
  out.sort();
  return out;
}

export class FileSystemAccessSource implements ProjectSource {
  readonly id = "fs-access" as const;
  readonly capabilities = FS_CAPABILITIES;
  private root: FileSystemDirectoryHandle | null = null;

  async open(): Promise<ProjectHandle> {
    if (typeof window === "undefined" || typeof window.showDirectoryPicker !== "function") {
      throw new Error("File System Access API nicht verfügbar (Chromium erforderlich) — nutze stattdessen die CLI-Quelle.");
    }
    const handle = await window.showDirectoryPicker({ id: "loumy-project", mode: "readwrite" });
    // Read-Write-Berechtigung sicherstellen (manche Browser geben den Picker mit read-only zurück).
    if (typeof handle.requestPermission === "function") {
      const state = await handle.requestPermission({ mode: "readwrite" });
      if (state !== "granted") throw new Error("Schreib-/Lese-Berechtigung für den Projekt-Ordner verweigert.");
    }
    this.root = handle;
    return { id: handle.name, name: handle.name };
  }

  private requireRoot(): FileSystemDirectoryHandle {
    if (!this.root) throw new Error("Kein Projekt geöffnet (open() zuerst aufrufen).");
    return this.root;
  }

  readFile(path: string): Promise<Uint8Array> {
    return fsReadFile(this.requireRoot(), path);
  }

  writeFile(path: string, data: Uint8Array): Promise<void> {
    return fsWriteFile(this.requireRoot(), path, data);
  }

  list(glob: string): Promise<string[]> {
    return fsList(this.requireRoot(), glob);
  }

  /** Über den Port hinaus (Multi-Doc-Host braucht Löschen) — Pfad sandboxed. */
  removeFile(path: string): Promise<void> {
    return fsRemoveFile(this.requireRoot(), path);
  }
}

// Re-Export der Sandbox-Helfer, damit der DocumentBar/Host direkt darauf prüfen kann.
export { normalizeProjectPath, dirOf, baseName };
